import React, { useState } from 'react';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { useLocale, SupportedLocale } from '@platform/core';

const LANGUAGES: { code: SupportedLocale; label: string }[] = [
  { code: 'he', label: 'עברית' },
  { code: 'en', label: 'English' },
];

export default function LoginPage() {
  const { signInWithGoogle, signInWithMicrosoft, signInWithEmail, signUp, isLoading, error } = useSimpleAuth();
  const { t, locale, setLocale } = useLocale();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    try {
      if (isSignUp) {
        await signUp(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err: any) {
      setLocalError(err.message || 'Authentication failed');
    }
  };

  const displayError = localError || (error ? error.message : null);

  return (
    <div className="login-page">
      <div style={{ position: 'absolute', top: '1.5rem', insetInlineEnd: '1.5rem' }}>
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value as SupportedLocale)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            background: 'white',
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          {LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.label}
            </option>
          ))}
        </select>
      </div>
      <div className="login-card">
        <h1 className="login-title">TurnKeyOS<span style={{color:'#3b82f6'}}>.AI</span></h1>
        <p style={{fontSize:'14px',color:'#64748b'}}>Planning</p>
        <p className="login-subtitle">
          {t('login.subtitle')}
        </p>

        {displayError && (
          <div
            style={{
              background: '#ffebee',
              color: '#c62828',
              padding: 'var(--spacing-md)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-md)',
              fontSize: '0.875rem',
            }}
          >
            {displayError}
          </div>
        )}

        <form onSubmit={handleEmailSubmit}>
          <div className="form-group">
            <input
              type="email"
              className="form-input"
              placeholder={t('login.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              className="form-input"
              placeholder={t('login.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            style={{ width: '100%' }}
            disabled={isLoading}
          >
            {isLoading ? '...' : isSignUp ? t('common.signUp') : t('login.signIn')}
          </button>
        </form>

        <div style={{ margin: 'var(--spacing-lg) 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('login.or')}
        </div>

        <button
          onClick={signInWithGoogle}
          className="btn btn-secondary btn-lg"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-sm)',
          }}
          disabled={isLoading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {t('login.continueWithGoogle')}
        </button>

        <button
          onClick={signInWithMicrosoft}
          className="btn btn-secondary btn-lg"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-sm)',
          }}
          disabled={isLoading}
        >
          <svg width="18" height="18" viewBox="0 0 21 21">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          {t('login.continueWithMicrosoft')}
        </button>

        <div style={{ marginTop: 'var(--spacing-lg)', textAlign: 'center' }}>
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary-color)',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {isSignUp ? t('auth.alreadyHaveAccount') : t('login.noAccount')}
          </button>
        </div>
      </div>
    </div>
  );
}
